import {useContext, useState, useEffect} from "react";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {GlobalContext} from "../../GlobalReducer";
import {CustomerInterface} from "../../interfaces/customer/CustomerInterface";
import {CustomerManagerInterface} from "../../interfaces/customer/CustomerManagerInterface";
import ManagerEditor from "./ManagerEditor";
import * as CustomerAPI from "../../api/customer";
import {PencilFill, Plus, Trash} from "react-bootstrap-icons";

export default function Account() {
    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const [managers, setManagers] = useState<CustomerManagerInterface[]>([]);
    const [editingManager, setEditingManager] = useState<CustomerManagerInterface | null>(null);
    const [showModal, setShowModal] = useState(false);

    const customer = state.customer as CustomerInterface;

    // Инициализация списка менеджеров из customer
    useEffect(() => {
        if (customer?.customer_managers) {
            setManagers(customer.customer_managers);
        }
    }, [customer]);

    const handleEdit = (manager: CustomerManagerInterface | null) => {
        setEditingManager(manager);
        setShowModal(true);
    };

    const handleAdd = () => {
        handleEdit(null);
    };

    const handleRemove = async (manager: CustomerManagerInterface) => {
        if (!window.confirm(`Вы уверены, что хотите удалить менеджера ${manager.name}?`)) {
            return;
        }

        try {
            await CustomerAPI.removeManager(customer, manager);
            const updatedManagers = managers.filter(m => m.id !== manager.id);
            setManagers(updatedManagers);

            // Обновляем глобальное состояние
            dispatch({
                type: 'SET_CUSTOMER',
                payload: {
                    ...customer,
                    customer_managers: updatedManagers
                }
            });
        } catch (error) {
            alert('Мы не смогли удалить менеджера');
        }
    };

    const handleSave = async (manager: CustomerManagerInterface) => {
        try {
            const savedManager = await CustomerAPI.saveManager(customer, manager);

            let updatedManagers;
            if (managers.find(m => m.id === savedManager.id)) {
                // Обновление существующего менеджера
                updatedManagers = managers.map(m =>
                    m.id === savedManager.id ? savedManager : m
                );
            } else {
                // Добавление нового менеджера
                updatedManagers = [...managers, savedManager];
            }

            setManagers(updatedManagers);

            // Обновляем глобальное состояние
            dispatch({
                type: 'SET_CUSTOMER',
                payload: {
                    ...customer,
                    customer_managers: updatedManagers
                }
            });

            setShowModal(false);
            setEditingManager(null);
        } catch (error: any) {
            alert('Мы не смогли сохранить менеджера: ' + (error.message || ''));
        }
    };

    const handleClose = () => {
        setShowModal(false);
        setEditingManager(null);
    };

    return (
        <div>
            <h1 className="bs-head">Мои данные</h1>

            <div className="box">
                {customer && <div className="box-body">
                    <div className="row">
                        <div className="col-md-12">
                            <h3>Общая информация</h3>
                            <dl className="row">
                                <dt className="col-sm-3">ID:</dt>
                                <dd className="col-sm-9">{customer.id}</dd>
                                <dt className="col-sm-3">Название:</dt>
                                <dd className="col-sm-9">{customer.name}</dd>
                                <dt className="col-sm-3">ИНН:</dt>
                                <dd className="col-sm-9">{customer.inn}</dd>
                                {customer.kpp && (
                                    <>
                                        <dt className="col-sm-3">КПП:</dt>
                                        <dd className="col-sm-9">{customer.kpp}</dd>
                                    </>
                                )}
                                <dt className="col-sm-3">Адрес:</dt>
                                <dd className="col-sm-9">{customer.address}</dd>
                                <dt className="col-sm-3">Склад:</dt>
                                <dd className="col-sm-9">{customer.stock_id}</dd>
                            </dl>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <h3>Лицевые счета</h3>
                            {customer.accounts.map((account, index) => (
                                <dl className="row" key={account.id || index}>
                                    <dt className="col-sm-3">ID:</dt>
                                    <dd className="col-sm-9">{account.id}</dd>
                                    <dt className="col-sm-3">Название:</dt>
                                    <dd className="col-sm-9">{account.name}</dd>
                                    <dt className="col-sm-3">Менеджер:</dt>
                                    <dd className="col-sm-9">{account.manager.name}</dd>
                                    <dt className="col-sm-3">Телефон:</dt>
                                    <dd className="col-sm-9">{account.manager.phone_additional}</dd>
                                    <dt className="col-sm-3">E-mail:</dt>
                                    <dd className="col-sm-9">
                                        <a href={`mailto:${account.manager.email}`}>
                                            {account.manager.email}
                                        </a>
                                    </dd>
                                    <dt className="col-sm-3">Тип доставки:</dt>
                                    <dd className="col-sm-9">{account.ship_type}</dd>
                                </dl>
                            ))}
                        </div>
                        <div className="col-md-12">
                            <h3>Грузополучатели</h3>
                            {customer.consignees.map((consignee, index) => (
                                <dl className="row" key={consignee.id || index}>
                                    <dt className="col-sm-3">ID:</dt>
                                    <dd className="col-sm-9">{consignee.id}</dd>
                                    <dt className="col-sm-3">Название:</dt>
                                    <dd className="col-sm-9">{consignee.name}</dd>
                                    <dt className="col-sm-3">Адрес:</dt>
                                    <dd className="col-sm-9">{consignee.address}</dd>
                                </dl>
                            ))}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <h3>Менеджеры</h3>
                            <div className="text-end">
                                <button className="btn btn-danger" onClick={handleAdd}>
                                    <Plus /> Добавить менеджера
                                </button>
                            </div>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>ФИО</th>
                                        <th>Email</th>
                                        <th>Телефон</th>
                                        <th>Активен?</th>
                                        <th>&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {managers.map((manager, index) => (
                                        <tr key={manager.id || index}>
                                            <td>{manager.id}</td>
                                            <td>{manager.name}</td>
                                            <td>{manager.email}</td>
                                            <td>{manager.phone}</td>
                                            <td>{manager.is_active ? 'да' : 'нет'}</td>
                                            <td className="text-right">
                                                <PencilFill
                                                    className="text-success"
                                                    style={{marginRight: '15px', cursor: 'pointer'}}
                                                    onClick={() => handleEdit(manager)}
                                                />
                                                <Trash
                                                    className="text-danger"
                                                    style={{cursor: 'pointer'}}
                                                    onClick={() => handleRemove(manager)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>}
            </div>

            {showModal && (
                <ManagerEditor
                    manager={editingManager}
                    onSave={handleSave}
                    onClose={handleClose}
                />
            )}
        </div>
    );
}